import { useEffect, useRef, useState } from 'react';

// Görselin üzerinde gerçek SU DALGASI (refraksiyon) efekti — drinkzoi.co tarzı.
// Fare görselin üzerinde gezdikçe, yükseklik-alanı (heightfield) dalga denklemi
// ile su damlaları yayılır; dalga eğiminden görsel örnekleme UV'si saptırılarak
// gerçek su kırılması + ışık parıltısı elde edilir (WebGL, ping-pong FBO).
//
// CORS gerektirir (lh3.googleusercontent.com destekler). WebGL/half-float yoksa,
// dokunmatik cihazda veya hareket azaltma açıkken düz <img>'e döner.

const DAMPING = 0.985; // dalga sönümü (1'e yakın = uzun süren dalgalar)

type Props = {
  src?: string;
  alt?: string;
  /** Hem canvas hem yedek <img>'e uygulanan konum sınıfları. */
  className?: string;
  /** Yalnızca yedek <img> için ek sınıf (ör. object-cover, scale). */
  imgClassName?: string;
};

const WaterImage = ({
  src,
  alt = '',
  className = '',
  imgClassName = '',
}: Props) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [active, setActive] = useState(false); // WebGL efekti çalışıyor mu

  useEffect(() => {
    setActive(false);
    if (!src) return;
    const reduce = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;
    const fine = window.matchMedia('(pointer: fine)').matches;
    if (reduce || !fine) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const gl = canvas.getContext('webgl', {
      alpha: true,
      premultipliedAlpha: false,
      antialias: false,
      depth: false,
      stencil: false,
    });
    if (!gl) return;

    const half = gl.getExtension('OES_texture_half_float');
    const linear = gl.getExtension('OES_texture_half_float_linear');
    if (!half) return;
    const HALF = half.HALF_FLOAT_OES;
    const RGBA = gl.RGBA;

    // Half-float'a render edilebiliyor mu?
    const renderable = (() => {
      const tex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texImage2D(gl.TEXTURE_2D, 0, RGBA, 4, 4, 0, RGBA, HALF, null);
      const fbo = gl.createFramebuffer();
      gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
      gl.framebufferTexture2D(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0,
        gl.TEXTURE_2D,
        tex,
        0,
      );
      const ok =
        gl.checkFramebufferStatus(gl.FRAMEBUFFER) === gl.FRAMEBUFFER_COMPLETE;
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.deleteTexture(tex);
      gl.deleteFramebuffer(fbo);
      return ok;
    })();
    if (!renderable) return;

    const simFilter = linear ? gl.LINEAR : gl.NEAREST;

    // ---- Shader derleme ----
    const compile = (type: number, source: string) => {
      const s = gl.createShader(type)!;
      gl.shaderSource(s, source);
      gl.compileShader(s);
      return s;
    };
    const makeProgram = (fs: string) => {
      const p = gl.createProgram()!;
      gl.attachShader(p, compile(gl.VERTEX_SHADER, baseVertex));
      gl.attachShader(p, compile(gl.FRAGMENT_SHADER, fs));
      gl.bindAttribLocation(p, 0, 'aPosition');
      gl.linkProgram(p);
      const u: Record<string, WebGLUniformLocation | null> = {};
      const n = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
      for (let i = 0; i < n; i++) {
        const name = gl.getActiveUniform(p, i)!.name;
        u[name] = gl.getUniformLocation(p, name);
      }
      return { program: p, u };
    };

    const baseVertex = `
      precision highp float;
      attribute vec2 aPosition;
      varying vec2 vUv; varying vec2 vL; varying vec2 vR; varying vec2 vT; varying vec2 vB;
      uniform vec2 texelSize;
      void main () {
        vUv = aPosition * 0.5 + 0.5;
        vL = vUv - vec2(texelSize.x, 0.0);
        vR = vUv + vec2(texelSize.x, 0.0);
        vT = vUv + vec2(0.0, texelSize.y);
        vB = vUv - vec2(0.0, texelSize.y);
        gl_Position = vec4(aPosition, 0.0, 1.0);
      }`;

    // Dalga denklemi: yeni = ((4 komşunun ort.) - önceki) * sönüm.
    // R = güncel yükseklik, G = bir önceki yükseklik.
    const updateFS = `
      precision highp float;
      varying vec2 vUv; varying vec2 vL; varying vec2 vR; varying vec2 vT; varying vec2 vB;
      uniform sampler2D uPrev; uniform float uDamping;
      void main () {
        float l = texture2D(uPrev, vL).r;
        float r = texture2D(uPrev, vR).r;
        float t = texture2D(uPrev, vT).r;
        float b = texture2D(uPrev, vB).r;
        float cur = texture2D(uPrev, vUv).r;
        float prv = texture2D(uPrev, vUv).g;
        float next = ((l + r + t + b) * 0.5 - prv) * uDamping;
        gl_FragColor = vec4(next, cur, 0.0, 1.0);
      }`;

    // İmleç konumuna damla ekle (güncel yüksekliği yükselt).
    const dropFS = `
      precision highp float;
      varying vec2 vUv;
      uniform sampler2D uPrev; uniform vec2 uCenter;
      uniform float uRadius; uniform float uStrength; uniform float uAspect;
      void main () {
        vec4 s = texture2D(uPrev, vUv);
        vec2 p = vUv - uCenter; p.x *= uAspect;
        float d = exp(-dot(p, p) / uRadius) * uStrength;
        s.r += d;
        gl_FragColor = s;
      }`;

    // Yükseklik eğiminden görsel UV'sini saptır (refraksiyon) + parıltı.
    const renderFS = `
      precision highp float;
      varying vec2 vUv; varying vec2 vL; varying vec2 vR; varying vec2 vT; varying vec2 vB;
      uniform sampler2D uHeight; uniform sampler2D uImage;
      uniform float uImageAspect; uniform float uCanvasAspect; uniform float uDisp;
      void main () {
        float l = texture2D(uHeight, vL).r;
        float r = texture2D(uHeight, vR).r;
        float t = texture2D(uHeight, vT).r;
        float b = texture2D(uHeight, vB).r;
        vec2 grad = vec2(r - l, b - t);
        // object-fit: cover
        vec2 scale = vec2(1.0);
        if (uCanvasAspect > uImageAspect) scale.y = uImageAspect / uCanvasAspect;
        else scale.x = uCanvasAspect / uImageAspect;
        vec2 uv = (vUv - 0.5) * scale + 0.5;
        uv += grad * uDisp;
        vec3 col = texture2D(uImage, uv).rgb;
        // su yüzeyi parıltısı
        vec3 n = normalize(vec3(-grad * 60.0, 1.0));
        float spec = pow(max(dot(n, normalize(vec3(0.4, 0.5, 1.0))), 0.0), 28.0);
        col += spec * 0.35;
        gl_FragColor = vec4(col, 1.0);
      }`;

    const updateProg = makeProgram(updateFS);
    const dropProg = makeProgram(dropFS);
    const renderProg = makeProgram(renderFS);

    // ---- Tam ekran quad ----
    const quad = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.bufferData(
      gl.ARRAY_BUFFER,
      new Float32Array([-1, -1, -1, 1, 1, 1, 1, -1]),
      gl.STATIC_DRAW,
    );
    const indices = gl.createBuffer();
    gl.bindBuffer(gl.ELEMENT_ARRAY_BUFFER, indices);
    gl.bufferData(
      gl.ELEMENT_ARRAY_BUFFER,
      new Uint16Array([0, 1, 2, 0, 2, 3]),
      gl.STATIC_DRAW,
    );
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
    gl.enableVertexAttribArray(0);

    // ---- Görsel dokusu (CORS) ----
    let destroyed = false;
    let raf = 0;
    const img = new Image();
    img.crossOrigin = 'anonymous';

    const cleanups: Array<() => void> = [];
    const cleanup = () => {
      destroyed = true;
      cancelAnimationFrame(raf);
      cleanups.forEach((c) => c());
    };

    img.onload = () => {
      if (destroyed) return;
      const imageTex = gl.createTexture();
      gl.bindTexture(gl.TEXTURE_2D, imageTex);
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      try {
        gl.texImage2D(gl.TEXTURE_2D, 0, RGBA, RGBA, gl.UNSIGNED_BYTE, img);
      } catch {
        cleanup(); // CORS tainted → düz img'e dön
        return;
      }
      const imageAspect = img.naturalWidth / img.naturalHeight;

      // ---- Sim FBO'ları ----
      type FBO = { tex: WebGLTexture; fbo: WebGLFramebuffer };
      let simW = 2;
      let simH = 2;
      const newFBO = (): FBO => {
        const tex = gl.createTexture()!;
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, simFilter);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, simFilter);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        gl.texImage2D(gl.TEXTURE_2D, 0, RGBA, simW, simH, 0, RGBA, HALF, null);
        const fbo = gl.createFramebuffer()!;
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
        gl.framebufferTexture2D(
          gl.FRAMEBUFFER,
          gl.COLOR_ATTACHMENT0,
          gl.TEXTURE_2D,
          tex,
          0,
        );
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        return { tex, fbo };
      };
      let read: FBO;
      let write: FBO;

      const resize = () => {
        const rect = canvas.getBoundingClientRect();
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        const cw = Math.max(1, Math.round(rect.width * dpr));
        const ch = Math.max(1, Math.round(rect.height * dpr));
        if (canvas.width !== cw || canvas.height !== ch) {
          canvas.width = cw;
          canvas.height = ch;
        }
        const nW = Math.min(320, Math.max(48, Math.round(rect.width * 0.55)));
        const nH = Math.min(420, Math.max(48, Math.round(rect.height * 0.55)));
        if (nW !== simW || nH !== simH) {
          simW = nW;
          simH = nH;
          read = newFBO();
          write = newFBO();
        }
      };
      resize();
      const ro = new ResizeObserver(resize);
      ro.observe(canvas);
      cleanups.push(() => ro.disconnect());

      const simTexel: [number, number] = [1 / simW, 1 / simH];
      const swap = () => {
        const t = read;
        read = write;
        write = t;
      };
      const drawTo = (fbo: WebGLFramebuffer | null, w: number, h: number) => {
        gl.bindFramebuffer(gl.FRAMEBUFFER, fbo);
        gl.viewport(0, 0, w, h);
        gl.drawElements(gl.TRIANGLES, 6, gl.UNSIGNED_SHORT, 0);
      };

      // ---- Pointer ----
      const pointer = { x: 0.5, y: 0.5, moved: false };
      const onMove = (e: PointerEvent) => {
        const rect = canvas.getBoundingClientRect();
        pointer.x = (e.clientX - rect.left) / rect.width;
        pointer.y = 1 - (e.clientY - rect.top) / rect.height;
        pointer.moved = true;
      };
      canvas.addEventListener('pointermove', onMove, { passive: true });
      cleanups.push(() => canvas.removeEventListener('pointermove', onMove));

      const addDrop = () => {
        gl.useProgram(dropProg.program);
        gl.uniform2f(dropProg.u.texelSize, simTexel[0], simTexel[1]);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, read.tex);
        gl.uniform1i(dropProg.u.uPrev, 0);
        gl.uniform2f(dropProg.u.uCenter, pointer.x, pointer.y);
        gl.uniform1f(dropProg.u.uRadius, 0.0009);
        gl.uniform1f(dropProg.u.uStrength, 0.55);
        gl.uniform1f(dropProg.u.uAspect, simW / simH);
        drawTo(write.fbo, simW, simH);
        swap();
      };

      const stepSim = () => {
        gl.useProgram(updateProg.program);
        gl.uniform2f(updateProg.u.texelSize, simTexel[0], simTexel[1]);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, read.tex);
        gl.uniform1i(updateProg.u.uPrev, 0);
        gl.uniform1f(updateProg.u.uDamping, DAMPING);
        drawTo(write.fbo, simW, simH);
        swap();
      };

      const renderToScreen = () => {
        gl.useProgram(renderProg.program);
        gl.uniform2f(renderProg.u.texelSize, simTexel[0], simTexel[1]);
        gl.activeTexture(gl.TEXTURE0);
        gl.bindTexture(gl.TEXTURE_2D, read.tex);
        gl.uniform1i(renderProg.u.uHeight, 0);
        gl.activeTexture(gl.TEXTURE1);
        gl.bindTexture(gl.TEXTURE_2D, imageTex);
        gl.uniform1i(renderProg.u.uImage, 1);
        gl.uniform1f(renderProg.u.uImageAspect, imageAspect);
        gl.uniform1f(renderProg.u.uCanvasAspect, canvas.width / canvas.height);
        gl.uniform1f(renderProg.u.uDisp, 0.45);
        drawTo(null, canvas.width, canvas.height);
      };

      const loop = () => {
        if (destroyed) return;
        if (pointer.moved) {
          pointer.moved = false;
          addDrop();
        }
        stepSim();
        renderToScreen();
        raf = requestAnimationFrame(loop);
      };
      setActive(true);
      raf = requestAnimationFrame(loop);
    };

    img.onerror = () => cleanup();
    img.src = src;

    return cleanup;
  }, [src]);

  return (
    <>
      <canvas
        ref={canvasRef}
        aria-hidden
        className={`${className} transition-opacity duration-500 ${
          active ? 'opacity-100' : 'opacity-0'
        }`}
      />
      {/* Yedek / efekt yokken görünen görsel (efekt aktifken canvas örter). */}
      {src && (
        <img
          src={src}
          alt={alt}
          referrerPolicy="no-referrer"
          loading="lazy"
          decoding="async"
          className={`${className} ${imgClassName} ${
            active ? 'opacity-0' : 'opacity-100'
          }`}
        />
      )}
    </>
  );
};

export default WaterImage;
